import type { ShopperProducts } from '@/scapi';

/** Flyout link types from category custom attribute `linkType` (SCAPI: `c_linkType`). */
export type CategoryLinkType = 'category' | 'page' | 'content' | 'folder' | 'external';

export type CategoryNavTarget = { kind: 'internal'; to: string } | { kind: 'external'; href: string };

type CategoryWithFlyoutLink = ShopperProducts.schemas['Category'] & {
    c_linkType?: CategoryLinkType | string;
    c_linkValue?: string;
};

const INTERNAL_PATH_BY_LINK_TYPE: Record<Exclude<CategoryLinkType, 'external'>, string> = {
    category: '/category',
    page: '/page',
    content: '/content',
    folder: '/folder',
};

function toInternalPath(basePath: string, linkValue: string): string {
    return linkValue.startsWith('/') ? linkValue : `${basePath}/${linkValue}`;
}

/**
 * Resolves a category flyout menu entry to an internal route or external URL.
 *
 * Uses BM custom attributes `linkType` / `linkValue` (SCAPI: `c_linkType` / `c_linkValue`).
 * Falls back to `/category/{category.id}` when link metadata is absent.
 */
export function getCategoryNavTarget(category: ShopperProducts.schemas['Category']): CategoryNavTarget {
    const { c_linkType: linkType, c_linkValue: linkValue } = category as CategoryWithFlyoutLink;

    if (linkType === 'external' && linkValue) {
        return { kind: 'external', href: linkValue };
    }

    if (linkValue) {
        const resolvedLinkType = (linkType ?? 'category') as CategoryLinkType;

        if (resolvedLinkType !== 'external' && resolvedLinkType in INTERNAL_PATH_BY_LINK_TYPE) {
            return {
                kind: 'internal',
                to: toInternalPath(INTERNAL_PATH_BY_LINK_TYPE[resolvedLinkType], linkValue),
            };
        }
    }

    return { kind: 'internal', to: `/category/${category.id}` };
}

/**
 * Navigates to a category flyout target (internal route or external URL).
 */
export function navigateToCategoryNavTarget(
    category: ShopperProducts.schemas['Category'],
    navigate: (to: string) => void
): void {
    const target = getCategoryNavTarget(category);

    if (target.kind === 'external') {
        window.location.assign(target.href);
        return;
    }

    navigate(target.to);
}
